import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Card, Col, Modal, Row, Space, Tag, Typography } from 'antd';
import { useEffect } from 'react';
import DocumentContentViewer from './DocumentContentViewer';
import EvidenceCard from './EvidenceCard';
import {
    useCloseEvidenceModal,
    useEvidenceModalData,
    useUpdateSelectedEvidence,
    useUpdateSelectedSourceFile,
} from './EvidenceState';

const { Text, Title } = Typography;

interface Props {
    renderValue?: () => React.ReactNode;
}

export default function EvidenceModal({ renderValue }: Props) {
    const { open, displayLabel, evidenceData, selectedEvidence } = useEvidenceModalData();
    const closeEvidenceModal = useCloseEvidenceModal();
    const updateSelectedSourceFile = useUpdateSelectedSourceFile();
    const updateSelectedEvidence = useUpdateSelectedEvidence();

    // Auto-select first evidence when modal opens
    useEffect(() => {
        if (open && !selectedEvidence && evidenceData) {
            // Select first supporting evidence, or first contradictory if no supporting
            const firstEvidence = evidenceData.supporting_evidence?.[0] || evidenceData.contradictory_evidence?.[0];
            if (firstEvidence) {
                updateSelectedEvidence(firstEvidence);
            }
        }
    }, [open, selectedEvidence, evidenceData, updateSelectedEvidence]);

    const isEvidenceSelected = (evidence: { source_file: string; snippet: string; explanation: string }) =>
        selectedEvidence !== null &&
        selectedEvidence.source_file === evidence.source_file &&
        selectedEvidence.snippet === evidence.snippet &&
        selectedEvidence.explanation === evidence.explanation;

    return (
        <Modal
            title={`Evidence: ${displayLabel}`}
            open={open}
            onCancel={() => {
                updateSelectedSourceFile(null as unknown as string);
                closeEvidenceModal();
            }}
            footer={null}
            width={1400}
            centered
            styles={{
                mask: { background: 'rgba(0,0,0,0.69)' },
                content: { borderRadius: 16 },
                body: { maxHeight: '85vh', overflow: 'hidden' },
            }}
        >
            <Row
                gutter={16}
                style={{ height: '75vh' }}
            >
                {/* Left Panel - Evidence */}
                <Col
                    span={selectedEvidence ? 7 : 24}
                    style={{ height: '100%', overflowY: 'auto', paddingRight: 8 }}
                >
                    {evidenceData ? (
                        <Space
                            direction="vertical"
                            size="small"
                            style={{ width: '100%' }}
                        >
                            {renderValue ? (
                                renderValue()
                            ) : (
                                <Card
                                    size="small"
                                    title="Value"
                                    style={{ background: '#f0f5ff', marginBottom: 12 }}
                                    styles={{
                                        body: { padding: 12 },
                                        header: { padding: '8px 12px', minHeight: 'auto' },
                                    }}
                                >
                                    <Tag
                                        color="blue"
                                        className="m-0 truncate rounded-lg"
                                        style={{
                                            fontSize: 13,
                                            maxWidth: 300,
                                            padding: '4px 8px',
                                        }}
                                    >
                                        {typeof evidenceData.final_value === 'object'
                                            ? JSON.stringify(evidenceData.final_value, null, 2)
                                            : evidenceData.final_value
                                              ? String(evidenceData.final_value)
                                              : '-'}
                                    </Tag>
                                </Card>
                            )}

                            {/* Supporting Evidence Section */}
                            {evidenceData.supporting_evidence?.length > 0 && (
                                <div>
                                    <Title
                                        level={5}
                                        style={{ color: '#52c41a', marginBottom: 8, fontSize: 14 }}
                                    >
                                        <CheckCircleOutlined /> Supporting Evidence (
                                        {evidenceData.supporting_evidence?.length})
                                    </Title>
                                    {evidenceData.supporting_evidence.map((evidence, index) => (
                                        <EvidenceCard
                                            key={index}
                                            evidence={evidence}
                                            isSelected={isEvidenceSelected(evidence)}
                                            onSelect={updateSelectedEvidence}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Contradictory Evidence Section */}
                            {evidenceData.contradictory_evidence?.length > 0 && (
                                <div>
                                    <Title
                                        level={5}
                                        style={{ color: '#ff4d4f', marginBottom: 8, marginTop: 12, fontSize: 14 }}
                                    >
                                        <CloseCircleOutlined /> Contradictory Evidence (
                                        {evidenceData.contradictory_evidence.length})
                                    </Title>
                                    {evidenceData.contradictory_evidence.map((evidence, index) => (
                                        <EvidenceCard
                                            key={index}
                                            evidence={evidence}
                                            isSelected={isEvidenceSelected(evidence)}
                                            onSelect={updateSelectedEvidence}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* No Evidence */}
                            {evidenceData.supporting_evidence?.length === 0 &&
                                evidenceData.contradictory_evidence?.length === 0 && (
                                    <Text type="secondary">No evidence available</Text>
                                )}
                        </Space>
                    ) : (
                        <Text type="secondary">No evidence data available for this field</Text>
                    )}
                </Col>

                {/* Right Panel - Document Viewer */}
                {selectedEvidence && (
                    <Col
                        span={17}
                        style={{
                            height: '100%',
                            borderLeft: '1px solid #f0f0f0',
                            paddingLeft: 16,
                            overflowY: 'auto',
                        }}
                    >
                        <DocumentContentViewer
                            sourceFile={selectedEvidence}
                            allSupportingEvidence={evidenceData?.supporting_evidence || []}
                            allContradictoryEvidence={evidenceData?.contradictory_evidence || []}
                        />
                    </Col>
                )}
            </Row>
        </Modal>
    );
}
